import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import { 
  Bot, Plus, Settings, Trash2, Play, Copy, Code, MessageSquare,
  Loader2, CheckCircle2, XCircle, Sparkles, Brain, Users,
  Palette, Layout, Shield, Zap, BarChart3, Edit, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAIAgents() {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [configOptions, setConfigOptions] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState('');
  const [embedCode, setEmbedCode] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'usuario',
    system_prompt: 'Eres un asistente amigable y profesional de OkReserva. Ayudas a los usuarios con sus consultas sobre reservaciones, horarios y servicios del restaurante.',
    greeting_message: '¡Hola! Soy el asistente de OkReserva. ¿En qué puedo ayudarte hoy?',
    models: ['meta-llama/llama-3.2-3b-instruct:free'],
    use_all_models: false,
    temperature: 0.7,
    max_tokens: 2048,
    skills: [],
    display_format: 'floating_button',
    theme: 'dark',
    primary_color: '#F97316',
    position: 'bottom-right',
    is_active: true,
    require_auth: false,
    allowed_roles: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [agentsRes, optionsRes] = await Promise.all([
        fetch(`${API_URL}/api/ai-agents/`, { headers }),
        fetch(`${API_URL}/api/ai-agents/config-options`, { headers })
      ]);

      if (agentsRes.ok) setAgents((await agentsRes.json()).agents || []);
      if (optionsRes.ok) setConfigOptions(await optionsRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai-agents/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Agente "${formData.name}" creado`);
        setShowCreateModal(false);
        resetForm();
        loadData();
      } else {
        toast.error(data.detail || 'Error al crear agente');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const updateAgent = async () => {
    if (!selectedAgent) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai-agents/${selectedAgent.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Agente actualizado');
        setShowEditModal(false);
        loadData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al actualizar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este agente?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai-agents/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Agente eliminado');
        loadData();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const testAgent = async () => {
    if (!testInput.trim() || !selectedAgent) return;
    
    setTesting(true);
    const userMessage = { role: 'user', content: testInput };
    setTestMessages([...testMessages, userMessage]);
    setTestInput('');

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai-agents/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_id: selectedAgent.id,
          messages: [...testMessages, userMessage].map(m => ({ role: m.role, content: m.content }))
        })
      });

      const data = await response.json();
      if (data.success) {
        setTestMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        toast.error(data.detail || 'Error en la respuesta');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTesting(false);
    }
  };

  const getEmbedCode = async (agent) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/ai-agents/${agent.id}/embed-code`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEmbedCode(data);
        setSelectedAgent(agent);
        setShowEmbedModal(true);
      }
    } catch (error) {
      toast.error('Error al obtener código');
    }
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      name: agent.name,
      description: agent.description || '',
      category: agent.category,
      system_prompt: agent.system_prompt,
      greeting_message: agent.greeting_message,
      models: agent.models || ['meta-llama/llama-3.2-3b-instruct:free'],
      use_all_models: agent.use_all_models || false,
      temperature: agent.temperature || 0.7,
      max_tokens: agent.max_tokens || 2048,
      skills: agent.skills || [],
      display_format: agent.display_format || 'floating_button',
      theme: agent.theme || 'dark',
      primary_color: agent.primary_color || '#F97316',
      position: agent.position || 'bottom-right',
      is_active: agent.is_active !== false,
      require_auth: agent.require_auth || false,
      allowed_roles: agent.allowed_roles || []
    });
    setShowEditModal(true);
  };

  const openTestModal = (agent) => {
    setSelectedAgent(agent);
    setTestMessages([{ role: 'assistant', content: agent.greeting_message }]);
    setTestInput('');
    setShowTestModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'usuario',
      system_prompt: 'Eres un asistente amigable y profesional de OkReserva. Ayudas a los usuarios con sus consultas sobre reservaciones, horarios y servicios del restaurante.',
      greeting_message: '¡Hola! Soy el asistente de OkReserva. ¿En qué puedo ayudarte hoy?',
      models: ['meta-llama/llama-3.2-3b-instruct:free'],
      use_all_models: false,
      temperature: 0.7,
      max_tokens: 2048,
      skills: [],
      display_format: 'floating_button',
      theme: 'dark',
      primary_color: '#F97316',
      position: 'bottom-right',
      is_active: true,
      require_auth: false,
      allowed_roles: []
    });
  };

  const toggleSkill = (skillId) => {
    const currentSkills = formData.skills || [];
    const skill = configOptions?.skills?.find(s => s.id === skillId);
    if (!skill) return;

    if (currentSkills.some(s => s.id === skillId)) {
      setFormData({ ...formData, skills: currentSkills.filter(s => s.id !== skillId) });
    } else {
      setFormData({ ...formData, skills: [...currentSkills, { ...skill, enabled: true }] });
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!configOptions?.openrouter_configured) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Brain className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-xl font-bold">OpenRouter no configurado</h2>
        <p className="text-muted-foreground text-center max-w-md">
          Para crear agentes IA, primero debes configurar OpenRouter con tu API key.
        </p>
        <Button asChild>
          <a href="/admin/openrouter">Configurar OpenRouter</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-agents-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center">
            <Bot className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Agentes IA</h1>
            <p className="text-muted-foreground">Crea y configura agentes de inteligencia artificial</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="gap-2">
          <Plus className="h-4 w-4" />
          Nuevo Agente
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{agents.length}</p>
                <p className="text-sm text-muted-foreground">Agentes</p>
              </div>
              <Bot className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{agents.filter(a => a.is_active).length}</p>
                <p className="text-sm text-muted-foreground">Activos</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{configOptions?.models?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Modelos</p>
              </div>
              <Brain className="h-8 w-8 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{agents.reduce((acc, a) => acc + (a.total_conversations || 0), 0)}</p>
                <p className="text-sm text-muted-foreground">Conversaciones</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agents Grid */}
      {agents.length === 0 ? (
        <Card className="py-12 text-center">
          <CardContent>
            <Bot className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No hay agentes creados</h3>
            <p className="text-muted-foreground mb-4">Crea tu primer agente IA para comenzar</p>
            <Button onClick={() => { resetForm(); setShowCreateModal(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Agente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent) => (
            <Card key={agent.id} className={`overflow-hidden transition-all ${!agent.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="h-10 w-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${agent.primary_color}20` }}
                    >
                      <Bot className="h-5 w-5" style={{ color: agent.primary_color }} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{agent.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{agent.category}</p>
                    </div>
                  </div>
                  <Badge variant={agent.is_active ? 'default' : 'secondary'}>
                    {agent.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {agent.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{agent.description}</p>
                )}
                
                <div className="flex flex-wrap gap-1">
                  {agent.skills?.slice(0, 3).map((skill) => (
                    <Badge key={skill.id} variant="outline" className="text-xs">
                      {skill.name}
                    </Badge>
                  ))}
                  {agent.skills?.length > 3 && (
                    <Badge variant="outline" className="text-xs">+{agent.skills.length - 3}</Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    {agent.total_conversations || 0} chats
                  </div>
                  <div className="flex items-center gap-1">
                    <Layout className="h-3 w-3" />
                    {agent.display_format === 'floating_button' ? 'Flotante' :
                     agent.display_format === 'embedded_window' ? 'Embebido' :
                     agent.display_format === 'sidebar' ? 'Sidebar' : 'Fullscreen'}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openTestModal(agent)}>
                    <Play className="h-3 w-3 mr-1" />
                    Probar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEditModal(agent)}>
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => getEmbedCode(agent)}>
                    <Code className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteAgent(agent.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => { 
        if (!open) { setShowCreateModal(false); setShowEditModal(false); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              {showEditModal ? 'Editar Agente' : 'Nuevo Agente IA'}
            </DialogTitle>
            <DialogDescription>
              Configura todos los aspectos del agente
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="mt-4">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="basic">Básico</TabsTrigger>
              <TabsTrigger value="ai">IA</TabsTrigger>
              <TabsTrigger value="skills">Skills</TabsTrigger>
              <TabsTrigger value="ui">UI</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del Agente *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Asistente de Reservaciones"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría *</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border bg-background"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {configOptions?.categories?.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Breve descripción del agente"
                />
              </div>
              <div className="space-y-2">
                <Label>Mensaje de Saludo</Label>
                <Textarea
                  value={formData.greeting_message}
                  onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                  placeholder="¡Hola! ¿En qué puedo ayudarte?"
                  rows={2}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Agente Activo</p>
                  <p className="text-sm text-muted-foreground">El agente estará disponible para usar</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </TabsContent>

            <TabsContent value="ai" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>System Prompt *</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  placeholder="Define el comportamiento y personalidad del agente..."
                  rows={6}
                />
                <p className="text-xs text-muted-foreground">
                  Este prompt define cómo se comportará el agente. Sé específico sobre su rol, tono y limitaciones.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Modelos de IA</Label>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-normal">Usar todos</Label>
                    <Switch
                      checked={formData.use_all_models}
                      onCheckedChange={(checked) => setFormData({ ...formData, use_all_models: checked })}
                    />
                  </div>
                </div>
                {!formData.use_all_models && (
                  <ScrollArea className="h-48 border rounded-lg p-2">
                    {configOptions?.models?.map((model) => (
                      <div key={model.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded">
                        <Checkbox
                          checked={formData.models.includes(model.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, models: [...formData.models, model.id] });
                            } else {
                              setFormData({ ...formData, models: formData.models.filter(m => m !== model.id) });
                            }
                          }}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{model.name}</p>
                          <p className="text-xs text-muted-foreground">{model.provider}</p>
                        </div>
                        {model.enabled && <Badge variant="outline" className="text-xs">Habilitado</Badge>}
                      </div>
                    ))}
                  </ScrollArea>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Temperatura: {formData.temperature}</Label>
                  <Slider
                    value={[formData.temperature]}
                    onValueChange={([value]) => setFormData({ ...formData, temperature: value })}
                    min={0}
                    max={1}
                    step={0.1}
                  />
                  <p className="text-xs text-muted-foreground">
                    Menor = más preciso, Mayor = más creativo
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Max Tokens: {formData.max_tokens}</Label>
                  <Slider
                    value={[formData.max_tokens]}
                    onValueChange={([value]) => setFormData({ ...formData, max_tokens: value })}
                    min={256}
                    max={4096}
                    step={256}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="skills" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Selecciona las habilidades que tendrá este agente
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {configOptions?.skills?.map((skill) => (
                  <Card
                    key={skill.id}
                    className={`cursor-pointer transition-all ${
                      formData.skills?.some(s => s.id === skill.id) ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => toggleSkill(skill.id)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={formData.skills?.some(s => s.id === skill.id)}
                          onCheckedChange={() => toggleSkill(skill.id)}
                        />
                        <div>
                          <p className="font-medium">{skill.name}</p>
                          <p className="text-sm text-muted-foreground">{skill.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="ui" className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Formato de Display</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border bg-background"
                    value={formData.display_format}
                    onChange={(e) => setFormData({ ...formData, display_format: e.target.value })}
                  >
                    {configOptions?.display_formats?.map((format) => (
                      <option key={format.id} value={format.id}>{format.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Posición</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border bg-background"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  >
                    <option value="bottom-right">Inferior Derecha</option>
                    <option value="bottom-left">Inferior Izquierda</option>
                    <option value="top-right">Superior Derecha</option>
                    <option value="top-left">Superior Izquierda</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tema</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border bg-background"
                    value={formData.theme}
                    onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  >
                    <option value="dark">Oscuro</option>
                    <option value="light">Claro</option>
                    <option value="auto">Auto (según sistema)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Color Primario</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Requiere Autenticación</p>
                  <p className="text-sm text-muted-foreground">Solo usuarios logueados pueden usar el agente</p>
                </div>
                <Switch
                  checked={formData.require_auth}
                  onCheckedChange={(checked) => setFormData({ ...formData, require_auth: checked })}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>
              Cancelar
            </Button>
            <Button onClick={showEditModal ? updateAgent : createAgent} disabled={saving || !formData.name || !formData.system_prompt}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {showEditModal ? 'Guardar Cambios' : 'Crear Agente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              Probar: {selectedAgent?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="border rounded-lg h-80 flex flex-col">
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {testMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {testing && (
                  <div className="flex justify-start">
                    <div className="bg-muted p-3 rounded-lg">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="border-t p-3 flex gap-2">
              <Input
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                placeholder="Escribe un mensaje..."
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && testAgent()}
              />
              <Button onClick={testAgent} disabled={testing || !testInput.trim()}>
                <Zap className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Embed Code Modal */}
      <Dialog open={showEmbedModal} onOpenChange={setShowEmbedModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5 text-primary" />
              Código de Integración
            </DialogTitle>
            <DialogDescription>
              Copia este código para integrar el agente en tu sitio web
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="script">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="script">Script</TabsTrigger>
              <TabsTrigger value="iframe">iFrame</TabsTrigger>
            </TabsList>

            <TabsContent value="script" className="space-y-2">
              <div className="relative">
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                  {embedCode?.script_embed}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(embedCode?.script_embed)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="iframe" className="space-y-2">
              <div className="relative">
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                  {embedCode?.iframe_embed}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(embedCode?.iframe_embed)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
